/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Trash2, UserX } from 'lucide-react';
import { SmsButton } from '@/components/DebtInsights';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CONTRACT_STATUS_LABEL, MATTER_KIND_LABEL, PARTY_KIND_LABEL, RELATION_LABEL } from '@kd/shared';
import { DocumentsTable } from '@/components/DocumentsTable';
import { FinAnalysis } from '@/components/FinAnalysis';
import { CaseChat } from '@/components/CaseChat';
import { PartyMonitor } from '@/pages/Monitoring';
import { PartyPortal } from '@/components/PartyPortal';
import { PartyForm } from '@/components/PartyForm';
import { PartyPicker } from '@/components/PartyPicker';
import { TasksTable } from '@/components/TasksTable';
import { Timeline } from '@/components/Timeline';
import { Badge, Button, Empty, ErrorText, Field, Input, KV, Modal, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { api, download, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtMoney, today } from '@/lib/format';

type Tab = 'info' | 'timeline' | 'contracts' | 'matters' | 'tasks' | 'documents' | 'fin' | 'chat';

export function PartyCardPage() {
  const { id } = useParams<{ id: string }>();
  const { can, hasModule } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('info');
  const [editing, setEditing] = useState(false);
  const { data: p, isLoading, error } = useQuery({ queryKey: ['party', id], queryFn: () => api.get(`/parties/${id}`) });
  const { data: cnt } = useQuery({ queryKey: ['party-counters', id], queryFn: () => api.get(`/parties/${id}/counters`) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['party', id] }); qc.invalidateQueries({ queryKey: ['parties'] }); };
  const remove = useMutation({ mutationFn: () => api.del(`/parties/${id}`), onSuccess: () => nav('/parties') });
  const anon = useMutation({ mutationFn: () => api.post(`/parties/${id}/pd/anonymize`), onSuccess: refresh });

  if (isLoading) return <Spinner />;
  if (error || !p) return <ErrorText error={error ?? new Error('Не найдено')} />;
  const w = can('party.write');

  return (
    <>
      <PageHeader title={p.name} subtitle={<span className="space-x-1">
        <span>{PARTY_KIND_LABEL[p.kind]}</span>{p.inn && <span>· ИНН {p.inn}</span>}
        {p.isClient && <Badge tone="brand">клиент</Badge>}{p.isCounterparty && <Badge>контрагент</Badge>}
        {p.tags.map((t: string) => <Badge key={t} tone="blue">{t}</Badge>)}
        {p.isClient && hasModule('M19') && <PayDiscipline partyId={id!} />}
      </span>} actions={<>
        {hasModule('M25') && can('party.write') && p.phones?.length > 0 && <SmsButton partyId={id!} />}
        {w && <Button variant="secondary" onClick={() => setEditing(true)}>Изменить</Button>}
        <Button variant="ghost" title="Выгрузить все сведения о лице (ст. 14 152-ФЗ)" onClick={() => download(`/parties/${id}/pd/export`)}><FileDown size={16} /></Button>
        {can('user.manage') && <Button variant="ghost" title="Обезличить по требованию субъекта" onClick={() => confirm('Обезличить лицо? ФИО, контакты и реквизиты будут удалены без возможности восстановления, доступ в кабинет и согласия отозваны.') && anon.mutate()}><UserX size={16} /></Button>}
        {can('party.delete') && <Button variant="ghost" onClick={() => confirm('Удалить карточку?') && remove.mutate()}><Trash2 size={16} /></Button>}
      </>} />
      <ErrorText error={anon.error} />
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[
        { id: 'info', label: 'Сведения' }, { id: 'timeline', label: 'Лента' },
        { id: 'contracts', label: 'Договоры', count: cnt?.contracts }, { id: 'matters', label: 'Дела', count: cnt?.matters },
        { id: 'tasks', label: 'Задачи', count: cnt?.open_tasks }, { id: 'documents', label: 'Документы', count: cnt?.documents },
        ...(p.kind !== 'PERSON' && hasModule('M15') ? [{ id: 'fin' as Tab, label: 'Финанализ' }] : []),
        ...(hasModule('M31') ? [{ id: 'chat' as Tab, label: 'Чат' }] : []),
      ]} />

      {tab === 'info' && (
        <div className="grid gap-5 xl:grid-cols-3">
          <section className="card p-4 xl:col-span-2">
            <KV items={[
              ['Краткое наименование', p.shortName], ['ОГРН', p.ogrn], ['КПП', p.kpp], ['Дата рождения', p.birthDate && fmtDate(p.birthDate)],
              ['Адрес', p.address], ['Почтовый адрес', p.postalAddress], ['Телефоны', p.phones.join(', ')], ['Почта', p.emails.join(', ')],
              ['Сайт', p.website], ['Источник', p.source], ['Создана', fmtDateTime(p.createdAt)],
            ]} />
            {p.notes && <div className="mt-4 whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm">{p.notes}</div>}
          </section>
          <div className="space-y-5">
            <Relations party={p} canWrite={w} onChange={refresh} />
            <BankAccounts party={p} canWrite={w} onChange={refresh} />
            <Consents party={p} canWrite={w} onChange={refresh} />
            {can('party.merge') && <Duplicates id={p.id} onMerged={refresh} />}
          </div>
        </div>
      )}
      {tab === 'info' && <div className="mt-5 grid gap-5 xl:grid-cols-2"><PartyMonitor partyId={id!} inn={p.inn} /><PartyPortal partyId={id!} /></div>}
      {tab === 'timeline' && <div className="card p-4"><Timeline path={`/parties/${id}/timeline`} canWrite={w} /></div>}
      {tab === 'fin' && <FinAnalysis partyId={id!} inn={p.inn} />}
      {tab === 'chat' && <CaseChat partyId={id} />}
      {tab === 'contracts' && <PartyContracts partyId={id!} />}
      {tab === 'matters' && <PartyMatters partyId={id!} />}
      {tab === 'tasks' && <TasksTable filter={{ partyId: id }} defaults={{ partyId: id }} />}
      {tab === 'documents' && <DocumentsTable filter={{ partyId: id }} />}

      <Modal title="Изменение карточки" open={editing} onClose={() => setEditing(false)} wide>
        <PartyForm party={p} onSaved={() => { setEditing(false); refresh(); }} />
      </Modal>
    </>
  );
}

function Relations({ party, canWrite, onChange }: { party: any; canWrite: boolean; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [toId, setToId] = useState<string | null>(null);
  const [toName, setToName] = useState<string>();
  const [type, setType] = useState('DIRECTOR');
  const add = useMutation({ mutationFn: () => api.post(`/parties/${party.id}/relations`, { toId, type }), onSuccess: () => { setAdding(false); setToId(null); onChange(); } });
  const del = useMutation({ mutationFn: (rid: string) => api.del(`/parties/${party.id}/relations/${rid}`), onSuccess: onChange });
  return (
    <section className="card p-4">
      <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold">Связанные лица</h3>
        {canWrite && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>Добавить</Button>}</div>
      {!party.relations.length ? <div className="text-sm text-slate-500">Нет</div> : (
        <ul className="space-y-1 text-sm">
          {party.relations.map((r: any) => (
            <li key={r.id} className="flex items-center justify-between gap-2">
              <span><Link className="text-brand-700 hover:underline" to={`/parties/${r.party_id}`}>{r.party_name}</Link>
                <span className="text-slate-500"> - {RELATION_LABEL[r.type]}{r.direction === 'in' ? ' (для этой карточки)' : ''}</span></span>
              {canWrite && <button className="text-slate-400 hover:text-red-600" onClick={() => del.mutate(r.id)} aria-label="Удалить"><Trash2 size={14} /></button>}
            </li>
          ))}
        </ul>
      )}
      <Modal title="Связанное лицо" open={adding} onClose={() => setAdding(false)}>
        <div className="space-y-3">
          <Field label="Кто"><PartyPicker value={toId} valueLabel={toName} onChange={(id, n) => { setToId(id); setToName(n); }} /></Field>
          <Field label="Кем приходится для этой карточки"><Select value={type} onChange={(e) => setType(e.target.value)} options={Object.entries(RELATION_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
          <ErrorText error={add.error} />
          <div className="flex justify-end"><Button disabled={!toId} onClick={() => add.mutate()}>Добавить</Button></div>
        </div>
      </Modal>
    </section>
  );
}

function BankAccounts({ party, canWrite, onChange }: { party: any; canWrite: boolean; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ bankName: '', bik: '', account: '', corrAccount: '' });
  const add = useMutation({ mutationFn: () => api.post(`/parties/${party.id}/bank-accounts`, f), onSuccess: () => { setAdding(false); onChange(); } });
  return (
    <section className="card p-4">
      <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold">Банковские счета</h3>
        {canWrite && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>Добавить</Button>}</div>
      {!party.bankAccounts.length ? <div className="text-sm text-slate-500">Нет</div> : party.bankAccounts.map((a: any) => (
        <div key={a.id} className="text-sm"><div>{a.bankName}</div><div className="text-xs text-slate-500 tabular-nums">р/с {a.account}, БИК {a.bik}</div></div>
      ))}
      <Modal title="Банковский счёт" open={adding} onClose={() => setAdding(false)}>
        <div className="space-y-3">
          <Field label="Банк"><Input value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="БИК"><Input value={f.bik} onChange={(e) => setF({ ...f, bik: e.target.value })} /></Field>
            <Field label="Корр. счёт"><Input value={f.corrAccount} onChange={(e) => setF({ ...f, corrAccount: e.target.value })} /></Field>
          </div>
          <Field label="Расчётный счёт"><Input value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} /></Field>
          <ErrorText error={add.error} />
          <div className="flex justify-end"><Button onClick={() => add.mutate()}>Сохранить</Button></div>
        </div>
      </Modal>
    </section>
  );
}

function Consents({ party, canWrite, onChange }: { party: any; canWrite: boolean; onChange: () => void }) {
  const add = useMutation({ mutationFn: (kind: string) => api.post(`/parties/${party.id}/consents`, { kind, givenAt: today(), source: 'Отмечено сотрудником' }), onSuccess: onChange });
  const revoke = useMutation({ mutationFn: (cid: string) => api.post(`/parties/${party.id}/consents/${cid}/revoke`), onSuccess: onChange });
  const active = (k: string) => party.consents.find((c: any) => c.kind === k && !c.revokedAt);
  return (
    <section className="card p-4">
      <h3 className="mb-2 text-sm font-semibold">Согласия (152-ФЗ)</h3>
      {[['PD_PROCESSING', 'Обработка персональных данных'], ['MARKETING', 'Рассылки']].map(([k, label]) => {
        const c = active(k);
        return (
          <div key={k} className="flex items-center justify-between py-1 text-sm">
            <span>{label}: {c ? <Badge tone="green">с {fmtDate(c.givenAt)}</Badge> : <Badge>нет</Badge>}</span>
            {canWrite && (c ? <Button size="sm" variant="ghost" onClick={() => revoke.mutate(c.id)}>Отозвать</Button>
              : <Button size="sm" variant="ghost" onClick={() => add.mutate(k)}>Отметить</Button>)}
          </div>
        );
      })}
    </section>
  );
}

function Duplicates({ id, onMerged }: { id: string; onMerged: () => void }) {
  const { data } = useQuery({ queryKey: ['dups', id], queryFn: () => api.get(`/parties/${id}/duplicates`) });
  const merge = useMutation({ mutationFn: (sourceId: string) => api.post(`/parties/${id}/merge`, { sourceId }), onSuccess: onMerged });
  if (!data?.length) return null;
  return (
    <section className="card border-amber-300 p-4">
      <h3 className="mb-2 text-sm font-semibold text-amber-800">Возможные дубли</h3>
      {data.map((d: any) => (
        <div key={d.id} className="flex items-center justify-between py-1 text-sm">
          <Link className="text-brand-700 hover:underline" to={`/parties/${d.id}`}>{d.name}</Link>
          <Button size="sm" variant="secondary" disabled={merge.isPending} onClick={() => confirm(`Перенести всё из «${d.name}» в эту карточку и удалить дубль?`) && merge.mutate(d.id)}>Объединить</Button>
        </div>
      ))}
      <ErrorText error={merge.error} />
    </section>
  );
}

function PartyContracts({ partyId }: { partyId: string }) {
  const { data } = useQuery({ queryKey: ['contracts', { partyId }], queryFn: () => api.get(`/contracts${qs({ partyId, pageSize: 100 })}`) });
  if (!data?.items.length) return <div className="card"><Empty>Договоров нет</Empty></div>;
  return (
    <div className="card overflow-x-auto"><table className="w-full">
      <thead className="bg-slate-50"><tr><th className="th">Договор</th><th className="th">Предмет</th><th className="th">Сумма</th><th className="th">Статус</th></tr></thead>
      <tbody>{data.items.map((c: any) => (
        <tr key={c.id} className="border-t border-slate-100"><td className="td"><Link className="text-brand-700 hover:underline" to={`/contracts/${c.id}`}>№ {c.number} от {fmtDate(c.date)}</Link></td>
          <td className="td">{c.subject}</td><td className="td tabular-nums">{fmtMoney(c.amount)}</td><td className="td">{CONTRACT_STATUS_LABEL[c.status]}</td></tr>
      ))}</tbody>
    </table></div>
  );
}

function PartyMatters({ partyId }: { partyId: string }) {
  const { data } = useQuery({ queryKey: ['matters', { clientId: partyId }], queryFn: () => api.get(`/matters${qs({ clientId: partyId, pageSize: 100 })}`) });
  if (!data?.items.length) return <div className="card"><Empty>Дел нет</Empty></div>;
  return (
    <div className="card overflow-x-auto"><table className="w-full">
      <thead className="bg-slate-50"><tr><th className="th">№</th><th className="th">Дело</th><th className="th">Вид</th><th className="th">Ответственный</th></tr></thead>
      <tbody>{data.items.map((m: any) => (
        <tr key={m.id} className="border-t border-slate-100"><td className="td tabular-nums">{m.number}</td>
          <td className="td"><Link className="text-brand-700 hover:underline" to={`/matters/${m.id}`}>{m.title}</Link></td>
          <td className="td">{MATTER_KIND_LABEL[m.kind]}</td><td className="td">{m.ownerName}</td></tr>
      ))}</tbody>
    </table></div>
  );
}

/** M19 платёжная дисциплина клиента по выставленным счетам */
function PayDiscipline({ partyId }: { partyId: string }) {
  const { data } = useQuery({ queryKey: ['client-score', partyId], queryFn: () => api.get<any>(`/scoring/clients/${partyId}`) });
  if (!data || data.score == null) return null;
  return <Badge tone={data.score >= 80 ? 'green' : data.score >= 50 ? 'amber' : 'red'}>{data.note}{data.overdue ? `, просрочено ${data.overdue}` : ''}</Badge>;
}
